// @ts-check
// Peças pequenas das telas dos fluxos (contrato 09 de fluxos, seção 17): chip da situação, círculo do ícone com a cor
// da categoria, link de contador que abre a lista dos cards, campo de formulário com o erro embaixo e o resumo de um
// passo já traduzido.
import { html } from 'htm/preact';
import { navigate } from '../../app/router.js';
import { isMockEnabled } from '../../data/api-client.js';
import { resolveFlowNodeDrilldown } from '../../data/flows.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatNumber } from '../../lib/format.js';
import { flowCountHref } from '../../lib/flow-drilldown.js';
import { isPlainActivation, rememberDrilldownResult } from '../dashboards/drilldown.js';
import { FLOW_CATEGORY_COLORS, flowNodeCategory, flowNodeIcon } from '../../../../shared/flows/flow-catalog.js';
import { flowSummaryText, flowSummaryTitle, summarizeFlowNode } from '../../../../shared/flows/flow-summary.js';
import { describeWaitResume } from '../../../../shared/flows/flow-wait-time.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowSummaryLookups} FlowSummaryLookups
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowSummaryPart} FlowSummaryPart
 */

/**
 * Texto de uma pendência: o motivo próprio quando existe (`flows.problemReasons.<CÓDIGO>.<motivo>`), senão o do código
 * com os detalhes (`flows.problems.<CÓDIGO>`).
 * @param {import('../../../../shared/flows/flow-problems.js').FlowProblem} problem
 */
export function flowProblemText(problem) {
  const details = /** @type {Record<string, unknown>} */ (problem.details ?? {});
  const reason = typeof details.reason === 'string' ? details.reason : null;
  if (reason && hasTranslation(`flows.problemReasons.${problem.code}.${reason}`)) return t(`flows.problemReasons.${problem.code}.${reason}`, details);
  if (hasTranslation(`flows.problems.${problem.code}`)) return t(`flows.problems.${problem.code}`, details);
  return t('flows.problems.UNKNOWN');
}

/**
 * Erro de um campo do formulário do painel: "Este campo não pode ficar vazio." para o que falta, ou o texto da
 * pendência que aponta o campo.
 * @param {import('../../../../shared/flows/flow-problems.js').FlowProblem[]} problems @param {string} field
 * @returns {string | null}
 */
export function fieldProblemText(problems, field) {
  const problem = problems.find((candidate) => candidate.details?.field === field || candidate.details?.fieldName === field);
  if (!problem) return null;
  return problem.code === 'NODE_INCOMPLETE' || problem.code === 'TRIGGER_INCOMPLETE' ? t('flows.panel.required') : flowProblemText(problem);
}

/**
 * Chip da situação: Rascunho (contorno), Ativo (verde), Inativo (cinza); inativo pelo sistema leva o alerta e o motivo
 * (o detalhe, como o modelo reclassificado pela Meta, vence o motivo geral quando tem texto).
 * @param {{ status: string, inactiveReason?: string | null, inactiveDetails?: Record<string, unknown> | null }} props
 */
export function FlowStatusChip({ status, inactiveReason = null, inactiveDetails = null }) {
  const detailReason = typeof inactiveDetails?.reason === 'string' ? inactiveDetails.reason : null;
  const reasonText = status !== 'INACTIVE' || !inactiveReason ? null
    : detailReason && hasTranslation(`flows.inactiveReasonDetails.${detailReason}`) ? t(`flows.inactiveReasonDetails.${detailReason}`)
      : t(`flows.inactiveReasons.${inactiveReason}`);
  return html`<span class="flow-status-chip" data-status=${status} title=${reasonText ?? undefined}>
    ${reasonText ? html`<${Icon} name="IconAlertCircle" size=${12} />` : null}
    <span>${t(`flows.status.${status}`)}</span>
  </span>`;
}

/** Variáveis de cor da categoria de um passo (tokens do tema, claro e escuro). @param {string} type */
export function categoryStyle(type) {
  const category = flowNodeCategory(type);
  const colors = category ? FLOW_CATEGORY_COLORS[category] : null;
  return colors ? { '--flow-category-color': `var(${colors.background})`, '--flow-category-icon-color': `var(${colors.icon})` } : {};
}

/** Círculo do ícone com a cor da categoria. @param {{ type: string, config?: Record<string, any> | null, size?: number }} props */
export function FlowCategoryCircle({ type, config = null, size = 32 }) {
  return html`<span class="flow-category-circle" data-size=${size} data-category=${flowNodeCategory(type) ?? undefined} style=${categoryStyle(type)} aria-hidden="true">
    <${Icon} name=${flowNodeIcon(type, config)} size=${size >= 32 ? 16 : 14} />
  </span>`;
}

/**
 * Partes do resumo de um passo, com o destaque em negrito e o link do modelo de e-mail (abre em outra aba).
 * @param {{ parts: FlowSummaryPart[], interactive?: boolean }} props
 */
export function FlowSummaryParts({ parts, interactive = true }) {
  return html`${parts.map((part, position) => {
    const text = part.key ? t(part.key, part.params ?? {}) : part.text ?? '';
    const spacer = position > 0 && !part.attach ? ' ' : '';
    if (part.link?.kind === 'EMAIL_TEMPLATE' && interactive) {
      return html`${spacer}<a class="flow-summary-link" href=${`/campaigns/templates/email/${encodeURIComponent(part.link.id)}`} target="_blank" rel="noopener"
        tabindex="-1" onClick=${(/** @type {MouseEvent} */ event) => event.stopPropagation()}>${text}</a>`;
    }
    return part.strong ? html`${spacer}<strong>${text}</strong>` : html`${spacer}<span>${text}</span>`;
  })}`;
}

/** Título e resumo de um passo, traduzidos. @param {FlowNode} node @param {FlowSummaryLookups} lookups */
export function describeNode(node, lookups) {
  const summary = summarizeFlowNode(node, lookups);
  return { summary, title: flowSummaryTitle(summary, t), text: flowSummaryText(summary, t) };
}

/**
 * Link de um contador: abre a lista dos cards daquele ponto. Número zero fica como texto (nada a listar).
 * @param {{ flowId: string, nodeKey: string, metric: string, value: number, label: string, icon?: string | null, tone?: string,
 *   className?: string, period?: { from?: string | null, to?: string | null }, tabIndex?: number, children?: any }} props
 */
export function FlowCountLink({ flowId, nodeKey, metric, value, label, icon = null, tone, className = 'flow-count-pill', period = {}, tabIndex, children }) {
  const href = flowCountHref(flowId, nodeKey, metric, value, { ...period, mock: isMockEnabled() });
  const content = html`${icon ? html`<${Icon} name=${icon} size=${12} />` : null}${children ?? html`<span>${formatNumber(value)}</span>`}`;
  if (!href) {
    return html`<span class=${className} data-tone=${tone} data-empty="true" title=${label} aria-label=${label}>${content}</span>`;
  }
  const open = async (/** @type {MouseEvent} */ event) => {
    event.stopPropagation();
    if (!isPlainActivation(event)) return;
    event.preventDefault();
    const element = /** @type {HTMLElement} */ (event.currentTarget);
    element.setAttribute('aria-busy', 'true');
    try {
      const data = await resolveFlowNodeDrilldown(flowId, nodeKey, { metric, ...(period.from ? { from: period.from } : {}), ...(period.to ? { to: period.to } : {}) });
      rememberDrilldownResult(data, `${window.location.pathname}${window.location.search}`);
      if (typeof data?.url === 'string' && data.url.startsWith('/')) navigate(data.url);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      element.removeAttribute('aria-busy');
    }
  };
  return html`<a class=${className} data-tone=${tone} href=${href} title=${label} aria-label=${label} tabindex=${tabIndex} onClick=${open}
    onMouseDown=${(/** @type {MouseEvent} */ event) => event.stopPropagation()}>${content}</a>`;
}

/**
 * Campo de formulário do painel: rótulo, controle, dica e o erro embaixo (borda vermelha pelo `data-invalid`).
 * @param {{ label: string, hint?: string | null, error?: string | null, children: any, htmlFor?: string, className?: string }} props
 */
export function FlowField({ label, hint = null, error = null, children, htmlFor, className = '' }) {
  return html`<div class=${`flow-field ${className}`} data-invalid=${error ? 'true' : undefined}>
    <label class="flow-field-label" for=${htmlFor}>${label}</label>
    ${children}
    ${error ? html`<span class="flow-field-error" role="alert">${error}</span>` : hint ? html`<span class="flow-field-hint">${hint}</span>` : null}
  </div>`;
}

/** Caixa informativa com o ícone. @param {{ icon?: string, tone?: 'info' | 'warning' | 'tip', children: any }} props */
export function FlowNotice({ icon = 'IconInfoCircle', tone = 'info', children }) {
  return html`<div class="flow-notice" data-tone=${tone}><${Icon} name=${icon} size=${16} /><div>${children}</div></div>`;
}

/**
 * Frase de uma espera: "Retoma hoje às 14:00", "Aguardando o limite diário do WhatsApp (retoma amanhã às 08:00)".
 * @param {{ waitKind?: string | null, resumeAt: string | null }} run @param {Date} [now]
 */
export function waitResumeText(run, now = new Date()) {
  const described = describeWaitResume({ waitKind: run.waitKind ?? null, resumeAt: run.resumeAt }, now.toISOString());
  if (!described.resumeAt) return t(hasTranslation(described.labelKey) ? described.labelKey : 'flows.waitResume.DURATION', { day: '', time: '', minutes: '' });
  const [, month, day] = (described.localDate ?? '').split('-');
  const dayText = described.relativeDay === 'PAST' ? t('flows.waitResume.days.PAST')
    : described.relativeDay === 'TODAY' ? t('flows.waitResume.days.TODAY')
      : described.relativeDay === 'TOMORROW' ? t('flows.waitResume.days.TOMORROW') : t('flows.waitResume.days.LATER', { date: `${day}/${month}` });
  const key = hasTranslation(described.labelKey) ? described.labelKey : 'flows.waitResume.DURATION';
  return t(key, { day: dayText, time: described.localTime ?? '', minutes: described.minutesUntil ?? 0 });
}

const REASON_NAMESPACES = Object.freeze({
  EXIT: ['flows.runs.exitReasons'],
  STOP: ['flows.runs.stopReasons'],
  ENTRY_SKIP: ['flows.runs.entrySkipReasons'],
  STEP_SKIP: ['flows.runs.stepReasons', 'deliveryReasons', 'flows.runs.skipReasons'],
  FAIL: ['flows.runs.failReasons'],
});

/**
 * Texto de um motivo (saída, parada, entrada ignorada, passo ignorado, falha), procurando nos textos da área e nos
 * motivos de envio compartilhados; motivo sem texto fica como "Outro motivo".
 * @param {keyof typeof REASON_NAMESPACES} kind @param {string | null | undefined} reason
 */
export function flowReasonText(kind, reason) {
  if (!reason) return t('flows.runs.otherReason');
  // a integração respondeu com um código HTTP (HTTP_500, HTTP_404)
  const httpStatus = kind === 'FAIL' ? /^HTTP_(\d{3})$/.exec(reason) : null;
  if (httpStatus) return t('flows.runs.failReasons.HTTP_STATUS', { status: httpStatus[1] });
  // o envio pelo WhatsApp falhou com o código de erro da Meta (131026, 131049)
  if (kind === 'FAIL' && /^\d+$/.test(reason)) return t('flows.runs.failReasons.META_ERROR', { code: reason });
  for (const namespace of REASON_NAMESPACES[kind] ?? []) {
    if (hasTranslation(`${namespace}.${reason}`)) return t(`${namespace}.${reason}`);
  }
  return t('flows.runs.otherReason');
}
